import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface KnowledgeRow {
  readonly documentExchangeId: string;
  readonly documentCode: string;
  readonly title: string;
  readonly documentType: string;
  readonly classification: string;
  readonly status: string;
}

const PAGE = 25;

/**
 * M42 §27 KnowledgePortal (§17). Self-service knowledge surface: stakeholders
 * browse the shared reports / compliance library and download entries. Viewing
 * needs M42_DOCUMENT_VIEW, downloading needs M42_DOCUMENT_DOWNLOAD.
 */
export function KnowledgePortal(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<KnowledgeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('documentType', 'REPORT');
    if (search.trim()) params.set('search', search.trim());
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: KnowledgeRow[]; total: number }>(`/v1/portal/documents?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, search, page, reload]);

  const download = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/portal/documents/${id}/download`, { method: 'POST', body: {}, token });
      setNotice(t('m42.common.downloadRecorded'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.knowledge.notAuthDownload'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="knowledge-portal">
      <h1>{t('m42.knowledge.title')}</h1>

      <section aria-label="knowledge-search">
        <label>{t('m42.knowledge.searchLabel')}<input aria-label="field-knowledge-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {actionError && <p role="alert" aria-label="knowledge-action-error">{actionError}</p>}
      {notice && <p aria-label="knowledge-notice">{notice}</p>}
      {error && <p role="alert" aria-label="knowledge-error">{error}</p>}

      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="knowledge-total">{t('m42.knowledge.total', { count: total })}</p>
          <DataTable
            ariaLabel="knowledge-table"
            rows={rows}
            rowKey={(d) => d.documentExchangeId}
            emptyMessage={t('m42.knowledge.empty')}
            columns={[
              { key: 'code', header: t('m42.common.code'), cellAriaLabel: (d) => `knowledge-code-${d.documentExchangeId}`, render: (d) => d.documentCode },
              { key: 'title', header: t('m42.common.title'), render: (d) => d.title },
              { key: 'class', header: t('m42.common.classCol'), render: (d) => d.classification },
              { key: 'status', header: t('m42.common.status'), render: (d) => d.status },
              { key: 'actions', header: t('m42.common.actions'), render: (d) => (
                <Button variant="secondary" type="button" aria-label={`download-${d.documentExchangeId}`} onClick={() => void download(d.documentExchangeId)}>{t('m42.common.download')}</Button>
              ) },
            ]}
          />

          <nav aria-label="knowledge-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m42.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m42.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m42.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
